import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Toast from '../../components/ui/Toast';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';

import PortfolioItemModal from './components/PortfolioItemModal';
import PortfolioItemCard from './components/PortfolioItemCard';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';

// Import the Supabase client directly
import supabase from '../../utils/supabase';

const VAPortfolioManagement = () => {
  const { userProfile, user, loading } = useAuth();
  const navigate = useNavigate();
  
  // Verify user has correct role for this page
  useEffect(() => {
    if (!loading && userProfile && userProfile.role !== 'va') {
      const redirectPath = userProfile.role === 'employer' ? '/employer-dashboard' : '/va-dashboard';
      navigate(redirectPath, { replace: true });
    }
  }, [user, userProfile, loading, navigate]);
  
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  const portfolioCategories = [
    'all', 'Administrative', 'Content Creation', 'Social Media', 'Marketing', 
    'Research', 'Design', 'Technical', 'Customer Service', 'Project Management'
  ];

  // Fetch portfolio items from database
  const fetchPortfolioItems = useCallback(async () => {
    // Don't attempt to fetch if user is not authenticated or still loading
    if (!user?.id || loading) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.from('portfolio_items')
        .select('*')
        .eq('user_id', user.id);
        
      if (error) {
        // Check if the error is due to missing table
        if (error.code === '42P01') { // PostgreSQL code for undefined_table
          if (import.meta.env.MODE === 'development') {
            setToast({
              show: true,
              message: 'Portfolio table not found. Using mock data for development.',
              type: 'warning'
            });
          } else {
            setToast({
              show: true,
              message: 'Portfolio feature is temporarily unavailable.',
              type: 'error'
            });
            setPortfolioItems([]);
            return;
          }
        } else {
          throw error;
        }
      } else {
        setPortfolioItems(data || []);
        return;
      }
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        setToast({
          show: true,
          message: 'Failed to load portfolio items. Using mock data.',
          type: 'error'
        });
        // If API fails, use mock data for development only
      } else {
        setToast({
          show: true,
          message: 'Unable to load portfolio items. Please try again later.',
          type: 'error'
        });
        setPortfolioItems([]);
        setIsLoading(false);
        return;
      }
      
      // Mock data only in development
      if (import.meta.env.MODE === 'development') {
        const mockPortfolioItems = [
        {
          id: 1,
          title: "E-commerce Website Content Strategy",
          category: "Content Creation",
          type: "website",
          thumbnail: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop",
          images: [
            "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
            "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop"
          ],
          description: "Comprehensive content strategy and copywriting for an e-commerce fashion brand.",
          fullDescription: "Developed and executed a complete content strategy for a growing e-commerce fashion brand. This included writing compelling product descriptions for over 200 items, creating a 3-month blog content calendar, and crafting email marketing campaigns that increased open rates by 35%.",
          client: "Fashion Forward Co.",
          completedDate: "2023-11-15",
          duration: "6 weeks",
          clientRating: 5,
          technologies: ["Google Docs", "WordPress", "Mailchimp", "SEMrush"],
          liveUrl: "https://example.com",
          sourceUrl: null,
          results: [
            "Increased organic traffic by 45%",
            "Improved email open rates by 35%",
            "Generated 200+ product descriptions"
          ],
          isPublic: true,
          isFeatured: true,
          createdAt: "2023-11-15T10:00:00Z"
        },
        {
          id: 2,
          title: "Social Media Campaign Management",
          category: "Social Media",
          type: "social",
          thumbnail: "https://images.unsplash.com/photo-1611926653458-09294b3142bf?w=400&h=300&fit=crop",
          images: [
            "https://images.unsplash.com/photo-1611926653458-09294b3142bf?w=800&h=600&fit=crop"
          ],
          description: "Complete social media management for a tech startup's product launch.",
          fullDescription: "Managed a comprehensive social media campaign for a tech startup's product launch across Instagram, LinkedIn, and Twitter. Created 50+ pieces of visual content, wrote engaging captions, and managed community engagement.",
          client: "InnovateTech",
          completedDate: "2023-10-20",
          duration: "8 weeks",
          clientRating: 5,
          technologies: ["Canva", "Hootsuite", "Adobe Creative Suite", "Analytics Tools"],
          results: [
            "300% increase in followers",
            "500% increase in engagement rates",
            "Created 50+ pieces of content"
          ],
          isPublic: true,
          isFeatured: false,
          createdAt: "2023-10-20T10:00:00Z"
        }
      ];
        setPortfolioItems(mockPortfolioItems);
      }
      
      setToast({
        show: true,
        message: 'Using mock data - database connection failed',
        type: 'warning'
      });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);
  
  useEffect(() => {
    fetchPortfolioItems();
  }, [fetchPortfolioItems]);

  const filteredItems = portfolioItems
    .filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.client?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'rating':
          return (b.clientRating || 0) - (a.clientRating || 0);
        case 'alphabetical':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

  const handleAddItem = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDeleteConfirmation = (itemId) => {
    setItemToDelete(itemId);
    setShowConfirmDialog(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    
    setActionInProgress(true);
    try {
      // Delete the item from the database
      const { error } = await supabase
        .from('portfolio_items')
        .delete()
        .eq('id', itemToDelete);
        
      if (error) throw error;
      
      // Update local state
      setPortfolioItems(prevItems => prevItems.filter(item => item.id !== itemToDelete));
      
      // Show success toast
      setToast({
        show: true,
        message: 'Portfolio item deleted successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting portfolio item:', error);
      
      // Show error toast
      setToast({
        show: true,
        message: 'Failed to delete portfolio item',
        type: 'error'
      });
    } finally {
      setActionInProgress(false);
      setShowConfirmDialog(false);
      setItemToDelete(null);
    }
  };

  const handleSaveItem = async (itemData) => {
    setIsLoading(true);
    setActionInProgress(true);
    
    try {
      let result;
      
      if (itemData.id) {
        // Update existing item
        const { data, error } = await supabase
          .from('portfolio_items')
          .update({
            ...itemData,
            updated_at: new Date().toISOString()
          })
          .eq('id', itemData.id)
          .eq('user_id', user.id) // Security check
          .select();
          
        if (error) throw error;
        result = data?.[0];
        
        // Update local state
        setPortfolioItems(prevItems => 
          prevItems.map(item => 
            item.id === itemData.id ? result : item
          )
        );
        
        setToast({
          show: true,
          message: 'Portfolio item updated successfully',
          type: 'success'
        });
      } else {
        // Add new item
        const { data, error } = await supabase
          .from('portfolio_items')
          .insert({
            ...itemData,
            user_id: user.id,
            created_at: new Date().toISOString()
          })
          .select();
          
        if (error) throw error;
        result = data?.[0];
        
        // Update local state
        setPortfolioItems(prevItems => [...prevItems, result]);
        
        setToast({
          show: true,
          message: 'Portfolio item added successfully',
          type: 'success'
        });
      }
      
      setShowModal(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving portfolio item:', error);
      setToast({
        show: true,
        message: 'Failed to save portfolio item',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      setActionInProgress(false);
    }
  };

  const handleToggleVisibility = async (itemId, isPublic) => {
    setActionInProgress(true);
    try {
      // Update the item in the database
      const { error } = await supabase
        .from('portfolio_items')
        .update({ is_public: isPublic })
        .eq('id', itemId);
        
      if (error) throw error;
      
      // Update local state
      setPortfolioItems(prevItems => 
        prevItems.map(item => 
          item.id === itemId ? { ...item, is_public: isPublic } : item
        )
      );
      
      // Show success toast
      setToast({
        show: true,
        message: `Portfolio item ${isPublic ? 'published' : 'unpublished'} successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating portfolio item visibility:', error);
      
      // Show error toast
      setToast({
        show: true,
        message: 'Failed to update portfolio item visibility',
        type: 'error'
      });
    } finally {
      setActionInProgress(false);
    }
  };

  const handleToggleFeatured = async (itemId, isFeatured) => {
    setActionInProgress(true);
    try {
      // Update the item in the database
      const { error } = await supabase
        .from('portfolio_items')
        .update({ is_featured: isFeatured })
        .eq('id', itemId);
        
      if (error) throw error;
      
      // Update local state
      setPortfolioItems(prevItems => 
        prevItems.map(item => 
          item.id === itemId ? { ...item, is_featured: isFeatured } : item
        )
      );
      
      // Show success toast
      setToast({
        show: true,
        message: `Portfolio item ${isFeatured ? 'featured' : 'unfeatured'} successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating portfolio item featured status:', error);
      
      // Show error toast
      setToast({
        show: true,
        message: 'Failed to update portfolio item featured status',
        type: 'error'
      });
    } finally {
      setActionInProgress(false);
    }
  };
  
    // Navigate to public portfolio view
  const handleViewPublicPortfolio = () => {
    navigate('/va-profile');
  };
  
  // Cancel delete confirmation
  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
    setItemToDelete(null);
  };

  // Helper function to handle both snake_case and camelCase fields
  const getFieldValue = (item, snakeCase, camelCase) => {
    // First check snake_case version, then camelCase version
    const value = item[snakeCase] !== undefined ? item[snakeCase] : item[camelCase];
    // Handle boolean values that might be stored as strings
    if (value === 'true') return true;
    if (value === 'false') return false;
    return !!value; // Convert to boolean
  };

  const featuredCount = portfolioItems.filter(item => getFieldValue(item, 'is_featured', 'isFeatured')).length;
  const publicCount = portfolioItems.filter(item => getFieldValue(item, 'is_public', 'isPublic')).length;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Portfolio Management - VA Sprouts</title>
        <meta name="description" content="Manage your VA portfolio items and showcase your work" />
      </Helmet>
      <VAHeaderNav title="Portfolio"/>

      <main className="pt-16 pb-20 md:pb-0">
        <div className="content-container py-8">
          
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <h1 className="text-2xl font-bold text-text-primary">Portfolio Management</h1>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="secondary"
                  iconName="Eye"
                  onClick={handleViewPublicPortfolio}
                  disabled={actionInProgress}
                >
                  View Public Portfolio
                </Button>
                <Button
                  variant="primary"
                  iconName="Plus"
                  onClick={handleAddItem}
                >
                  Add Portfolio Item
                </Button>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 border border-primary-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-primary mb-1">{portfolioItems.length}</div>
                    <div className="text-sm font-medium text-primary-700">Total Items</div>
                  </div>
                  <div className="p-3 bg-primary-200 rounded-full">
                    <Icon name="Briefcase" size={24} className="text-primary-700" />
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-success-50 to-success-100 rounded-xl p-6 border border-success-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-success mb-1">{publicCount}</div>
                    <div className="text-sm font-medium text-success-700">Public Items</div>
                  </div>
                  <div className="p-3 bg-success-200 rounded-full">
                    <Icon name="Eye" size={24} className="text-success-700" />
                  </div>
                </div>
                <div className="mt-2 text-xs text-success-600">
                  {portfolioItems.length > 0 ? Math.round((publicCount / portfolioItems.length) * 100) : 0}% of total
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-warning-50 to-warning-100 rounded-xl p-6 border border-warning-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-warning mb-1">{featuredCount}</div>
                    <div className="text-sm font-medium text-warning-700">Featured</div>
                  </div>
                  <div className="p-3 bg-warning-200 rounded-full">
                    <Icon name="Star" size={24} className="text-warning-700" />
                  </div>
                </div>
                <div className="mt-2 text-xs text-warning-600">
                  Highlighted items
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-xl p-6 border border-secondary-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-secondary mb-1">
                      {portfolioItems.length > 0 ? (portfolioItems.reduce((sum, item) => sum + (item.clientRating || 0), 0) / portfolioItems.length).toFixed(1) : 0}
                    </div>
                    <div className="text-sm font-medium text-secondary-700">Avg Rating</div>
                  </div>
                  <div className="p-3 bg-secondary-200 rounded-full">
                    <Icon name="Star" size={24} className="text-secondary-700" />
                  </div>
                </div>
                <div className="mt-2 text-xs text-secondary-600">
                  {portfolioItems.filter(item => item.clientRating).length} rated items
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-surface rounded-lg border border-border p-6 mb-8">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="flex-1">
                  <Input
                    placeholder="Search portfolio items..."
                    value={searchTerm}
                    onChange={setSearchTerm}
                    iconName="Search"
                  />
                </div>

                {/* Category Filter */}
                <div className="md:w-48">
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {portfolioCategories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sort */}
                <div className="md:w-40">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="rating">Highest Rated</option>
                    <option value="alphabetical">Alphabetical</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Portfolio Items Grid */}
            {filteredItems.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredItems.map((item) => (
                  <PortfolioItemCard
                    key={item.id}
                    item={item}
                    onEdit={() => handleEditItem(item)}
                    onDelete={() => handleDeleteConfirmation(item.id)}
                    onToggleVisibility={(isPublic) => handleToggleVisibility(item.id, isPublic)}
                    onToggleFeatured={(isFeatured) => handleToggleFeatured(item.id, isFeatured)}
                    disabled={actionInProgress}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary-100 rounded-full mb-4">
                  <Icon name="Briefcase" size={24} className="text-secondary-500" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  {searchTerm || filterCategory !== 'all' ? 'No items found' : 'No portfolio items yet'}
                </h3>
                <p className="text-text-secondary mb-6">
                  {searchTerm || filterCategory !== 'all' ?'Try adjusting your search criteria or filters' :'Start building your portfolio by adding your first project'
                  }
                </p>
                {(!searchTerm && filterCategory === 'all') && (
                  <Button
                    variant="primary"
                    iconName="Plus"
                    onClick={handleAddItem}
                  >
                    Add Your First Item
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
{/* Portfolio Item Modal */}
      <PortfolioItemModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaveItem}
        editingItem={editingItem}
        isLoading={isLoading}
        categories={portfolioCategories.filter(cat => cat !== 'all')}
        existingItems={portfolioItems}
      />
      
      {/* Toast Notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
          autoClose={3000}
        />
      )}
      
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <ConfirmationDialog
          title="Delete Portfolio Item"
          message="Are you sure you want to delete this portfolio item? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={handleDeleteItem}
          onCancel={handleCancelDelete}
          isLoading={actionInProgress}
          type="danger"
        />
      )}
    </div>
  );
};

export default VAPortfolioManagement;

